// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pure formatting for the task detail view (phone sheet and desktop modal).

import type {KonvoSubtask, KonvoTask, KonvoTaskStatus} from '@app/features/konvo_tasks/state/KonvoTasks';

/** Same rule as the store's isTaskClosed; kept here so this module stays free of app state. */
function isClosed(task: {status: Pick<KonvoTaskStatus, 'type'>}): boolean {
	return task.status.type === 'closed' || task.status.type === 'done';
}

/** ClickUp priorities 1–4, drawn in the calm palette of the refresh (no neon). */
export const TASK_PRIORITIES: ReadonlyArray<{id: number; color: string}> = [
	{id: 1, color: '#c9453c'},
	{id: 2, color: '#d49a24'},
	{id: 3, color: '#4f8fd6'},
	{id: 4, color: '#9a9aa3'},
];

export function priorityColor(id: number | null | undefined): string | null {
	return TASK_PRIORITIES.find((priority) => priority.id === id)?.color ?? null;
}

function startOfDay(date: Date): number {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** "Mon 28 Sept"; the year only when it is not this year. */
export function fmtDay(ms: number, now = new Date()): string {
	const date = new Date(ms);
	return date.toLocaleDateString(undefined, {
		weekday: 'short',
		day: 'numeric',
		month: 'short',
		year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
	});
}

export function fmtTime(ms: number): string {
	return new Date(ms).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});
}

/** Due label: the day, plus the time when the due date carries one. */
export function fmtDue(task: Pick<KonvoTask, 'due_date' | 'due_date_time'>, now = new Date()): string {
	if (task.due_date == null) return '';
	const day = fmtDay(task.due_date, now);
	return task.due_date_time ? `${day}, ${fmtTime(task.due_date)}` : day;
}

/** Red when the task is late, or due today or tomorrow. Closed tasks are never urgent. */
export function dueTone(
	task: Pick<KonvoTask, 'due_date' | 'due_date_time' | 'status'>,
	now = new Date(),
): 'late' | 'soon' | null {
	if (task.due_date == null || isClosed(task)) return null;
	const today = startOfDay(now);
	const dueDay = startOfDay(new Date(task.due_date));
	if (task.due_date_time ? task.due_date < now.getTime() : dueDay < today) return 'late';
	if (dueDay <= today + 86_400_000) return 'soon';
	return null;
}

/** "Today, 08:20" / "Yesterday, 17:40" / "22 Sept, 06:58". */
export function fmtStamp(ms: number | null, now = new Date()): string {
	if (ms == null) return '';
	const date = new Date(ms);
	const days = Math.round((startOfDay(date) - startOfDay(now)) / 86_400_000);
	const time = fmtTime(ms);
	if (days === 0 || days === -1) {
		const word = new Intl.RelativeTimeFormat(undefined, {numeric: 'auto'}).format(days, 'day');
		return `${word.charAt(0).toUpperCase()}${word.slice(1)}, ${time}`;
	}
	const day = date.toLocaleDateString(undefined, {
		day: 'numeric',
		month: 'short',
		year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
	});
	return `${day}, ${time}`;
}

/** "21 Sept" for system lines. */
export function fmtShortDay(ms: number | null, now = new Date()): string {
	if (ms == null) return '';
	const date = new Date(ms);
	return date.toLocaleDateString(undefined, {
		day: 'numeric',
		month: 'short',
		year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
	});
}

export function fmtRelative(ms: number | null, now = Date.now()): string {
	if (ms == null) return '';
	const rtf = new Intl.RelativeTimeFormat(undefined, {numeric: 'auto', style: 'short'});
	const minutes = Math.round((ms - now) / 60_000);
	if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
	const hours = Math.round(minutes / 60);
	if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
	const days = Math.round(hours / 24);
	if (Math.abs(days) < 7) return rtf.format(days, 'day');
	return fmtShortDay(ms);
}

/** Running timer readout: "0:24:10". */
export function fmtTimer(ms: number): string {
	const total = Math.max(0, Math.floor(ms / 1000));
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function fmtBytes(bytes: number | null): string {
	if (bytes == null) return '';
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Value for a native `datetime-local` input. */
export function toLocalInputValue(ms: number | null): string {
	if (ms == null) return '';
	const d = new Date(ms);
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function toDateInputValue(ms: number | null): string {
	return toLocalInputValue(ms).slice(0, 10);
}

export function toTimeInputValue(ms: number | null): string {
	return toLocalInputValue(ms).slice(11, 16);
}

/** Local date + optional "HH:MM" → epoch ms. Date-only due dates sit at 17:00 so they read as "that day". */
export function fromDateAndTime(date: string, time: string): number | null {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
	const [y, m, d] = date.split('-').map(Number) as [number, number, number];
	const [hh, mm] = /^\d{2}:\d{2}$/.test(time) ? (time.split(':').map(Number) as [number, number]) : [17, 0];
	const ms = new Date(y, m - 1, d, hh, mm).getTime();
	return Number.isFinite(ms) ? ms : null;
}

/** Quick due picks relative to now: today, tomorrow, next Monday, in a week. */
export function dueQuickPicks(now = new Date()): Array<{key: 'today' | 'tomorrow' | 'monday' | 'week'; ms: number}> {
	const at = (offsetDays: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + offsetDays, 17, 0);
	const toMonday = ((8 - now.getDay()) % 7 || 7) as number;
	return [
		{key: 'today', ms: at(0).getTime()},
		{key: 'tomorrow', ms: at(1).getTime()},
		{key: 'monday', ms: at(toMonday).getTime()},
		{key: 'week', ms: at(7).getTime()},
	];
}

export function subtaskProgress(subtasks: ReadonlyArray<Pick<KonvoSubtask, 'status'>>): {done: number; total: number} {
	return {done: subtasks.filter(isClosed).length, total: subtasks.length};
}

export function initialsOf(name: string | null | undefined): string {
	const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return '?';
	if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
	return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

/** First name for system lines ("Lerato set priority…"). */
export function firstName(name: string | null | undefined): string {
	return (name ?? '').trim().split(/\s+/)[0] ?? '';
}
